import * as z from "zod";
import { PlatformUserRefSchema } from "./platform-admin.js";

export const PLATFORM_MANAGED_CONFIGURATION_MESSAGE = "此配置由平台管理";

export const MoneySchema = z.object({
  amountMinor: z.number().int().positive().max(100_000_000),
  currency: z.enum(["CNY", "USD"]),
});
export type Money = z.infer<typeof MoneySchema>;

/** Model credit is denominated in micro USD, independently of the checkout currency. */
export const CommercialPlanSchema = z.object({
  id: z.string().min(1).max(100),
  version: z.number().int().positive(),
  name: z.string().trim().min(1).max(80),
  price: MoneySchema,
  durationDays: z.number().int().min(1).max(366),
  weeklyModelCreditMicros: z.number().int().nonnegative().max(10_000_000_000).optional(),
  modelCreditMicros: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
  resetPriceMinor: MoneySchema.shape.amountMinor.optional(),
  desktopSeconds: z.number().int().nonnegative().max(31_622_400).optional(),
  storageMib: z.number().int().min(256).max(1_048_576),
  memoryMib: z.number().int().min(256).max(65_536),
  cpuMillis: z.number().int().min(100).max(32_000),
  maxCpuMillis: z.number().int().min(100).max(32_000).optional(),
  maxMemoryMib: z.number().int().min(256).max(65_536).optional(),
  maxBots: z.number().int().min(1).max(1000).nullable(),
  testOnly: z.boolean(),
});
export type CommercialPlan = z.infer<typeof CommercialPlanSchema>;

export const CommercialPlanInputSchema = CommercialPlanSchema.omit({
  modelCreditMicros: true,
  desktopSeconds: true,
})
  .extend({ weeklyModelCreditMicros: z.number().int().nonnegative().max(10_000_000_000) })
  .strict();
export type CommercialPlanInput = z.infer<typeof CommercialPlanInputSchema>;

/**
 * Snapshots written before weekly quotas hold one grant for the whole period. Rounding undoes
 * the floor in the saved legacy value, so an unchanged plan compares equal after normalization.
 */
export function planWeeklyQuotaMicros(plan: CommercialPlan): number {
  if (plan.weeklyModelCreditMicros !== undefined) return plan.weeklyModelCreditMicros;
  if (plan.modelCreditMicros === undefined) return 0;
  return Math.round((plan.modelCreditMicros * 7) / plan.durationDays);
}

/** Normalize stored snapshots before editing or comparing versions. */
export function commercialPlanInput(plan: CommercialPlan): CommercialPlanInput {
  const { modelCreditMicros: _legacy, desktopSeconds: _desktop, ...input } = plan;
  return CommercialPlanInputSchema.parse({
    ...input,
    weeklyModelCreditMicros: planWeeklyQuotaMicros(plan),
  });
}

export const WINDOW_MS = 7 * 86_400_000;
export function creditWindow(period: { id: string; startsAt: Date; endsAt: Date }, now: Date) {
  if (now < period.startsAt || now >= period.endsAt) return null;
  const index = Math.floor((now.getTime() - period.startsAt.getTime()) / WINDOW_MS);
  const startsAt = new Date(period.startsAt.getTime() + index * WINDOW_MS);
  return {
    key: `${period.id}:${index}`,
    index,
    startsAt,
    endsAt: new Date(Math.min(startsAt.getTime() + WINDOW_MS, period.endsAt.getTime())),
  };
}

export function prorationCredit(priceMinor: number, startsAt: Date, endsAt: Date, now: Date) {
  if (now < startsAt || now >= endsAt) return 0;
  const total = Math.floor((endsAt.getTime() - startsAt.getTime()) / 1000);
  const remaining = Math.floor((endsAt.getTime() - now.getTime()) / 1000);
  return total > 0 ? Math.floor((priceMinor * remaining) / total) : 0;
}

/** Whole multiples from 2× up; below that one decimal, so 1.3× never reads as the base tier. */
export function quotaMultiple(plan: CommercialPlan, basePlan: CommercialPlan) {
  const base = planWeeklyQuotaMicros(basePlan);
  const quota = planWeeklyQuotaMicros(plan);
  if (base <= 0 || quota <= 0) return null;
  const ratio = quota / base;
  return ratio >= 2 ? Math.round(ratio) : Math.round(ratio * 10) / 10;
}

export function usedPercent(usedMicros: bigint | number, quotaMicros: bigint | number) {
  return quotaMicros <= 0
    ? 100
    : Math.min(100, Math.round((Number(usedMicros) / Number(quotaMicros)) * 100));
}

export const PurchaseOfferSchema = z.object({
  kind: z.enum(["subscribe", "renew", "upgrade", "next"]),
  available: z.boolean(),
  reason: z.literal("prepaid").optional(),
  amountMinor: MoneySchema.shape.amountMinor,
  creditMinor: z.number().int().nonnegative().max(100_000_000),
  currency: MoneySchema.shape.currency,
});
export const CommercialPlanPublicSchema = CommercialPlanSchema.omit({
  version: true,
  weeklyModelCreditMicros: true,
  modelCreditMicros: true,
  desktopSeconds: true,
}).extend({
  modelCreditMicros: z.literal(0),
  desktopSeconds: z.number().int().nonnegative(),
  quotaMultiple: z.number().nullable(),
  offer: PurchaseOfferSchema,
});
export type CommercialPlanPublic = z.infer<typeof CommercialPlanPublicSchema>;

type PlanResources = Pick<
  CommercialPlan,
  "cpuMillis" | "memoryMib" | "maxCpuMillis" | "maxMemoryMib"
>;

/** An explicit ceiling opts into sharing, including one equal to the reservation. */
export function planHasBurst(plan: PlanResources): boolean {
  return plan.maxCpuMillis !== undefined || plan.maxMemoryMib !== undefined;
}

export function planResourceCeiling(plan: PlanResources) {
  return {
    cpuMillis: plan.maxCpuMillis ?? plan.cpuMillis,
    memoryMib: plan.maxMemoryMib ?? plan.memoryMib,
  };
}

export const OrderStatusSchema = z.enum([
  "creating",
  "payable",
  "paid",
  "closing",
  "closed",
  "refunding",
  "refunded",
  "manual_review",
]);
export type CommercialOrderStatus = z.infer<typeof OrderStatusSchema>;
export const PaymentProviderIdSchema = z.enum(["alipay", "wechat"]);
export type PaymentProviderId = z.infer<typeof PaymentProviderIdSchema>;
export const CommercialOrderSchema = z.object({
  id: z.string(),
  plan: CommercialPlanSchema.pick({ id: true, name: true, price: true, durationDays: true }),
  kind: z.enum(["subscription", "upgrade", "reset"]),
  amountMinor: MoneySchema.shape.amountMinor,
  currency: MoneySchema.shape.currency,
  creditMinor: z.number().int().nonnegative(),
  provider: PaymentProviderIdSchema,
  status: OrderStatusSchema,
  checkoutUrl: z.string().url().nullable(),
  createdAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
});
export type CommercialOrder = z.infer<typeof CommercialOrderSchema>;
export const PlatformOrderSchema = CommercialOrderSchema.extend({
  plan: CommercialPlanSchema,
  userId: z.string(),
  user: PlatformUserRefSchema,
});
export const EntitlementStateSchema = z.enum([
  "unsubscribed",
  "active",
  "locked",
  "purging",
  "purged",
  "manual_review",
]);
export const CommercialAccessSchema = z.object({
  enabled: z.boolean(),
  state: EntitlementStateSchema,
  activeSpaceId: z.string().nullable(),
  workspaceGeneration: z.number().int().nonnegative(),
  entitlementRevision: z.number().int().nonnegative(),
  expiresAt: z.string().datetime().nullable(),
  modelCreditMicros: z.string(),
  desktopSecondsRemaining: z.number().int().nonnegative(),
  maxBots: z.number().int().nonnegative().nullable(),
  plan: z.object({ id: z.string(), name: z.string() }).nullable(),
  quota: z
    .object({
      usedPercent: z.number().int().min(0).max(100),
      resetsAt: z.string().datetime(),
      boundary: z.enum(["reset", "expiry"]),
      resetPriceMinor: MoneySchema.shape.amountMinor.nullable(),
      currency: MoneySchema.shape.currency,
    })
    .nullable(),
  /** Whether commercial.checkout would accept a new order for this account now. */
  checkoutOpen: z.boolean(),
  /** Configured payment providers, in order of preference. */
  paymentProviders: z.array(PaymentProviderIdSchema).optional(),
});
export const PlatformPermissionSchema = z.enum([
  "users.read",
  "users.manage",
  "nodes.read",
  "nodes.manage",
  "billing.read",
  "billing.manage",
  "models.manage",
  "integrations.manage",
  "requests.read",
  "requests.content",
  "operations.manage",
  "roles.manage",
]);
export type CommercialAccess = z.infer<typeof CommercialAccessSchema>;
export type PlatformPermission = z.infer<typeof PlatformPermissionSchema>;
export const PlatformRoleSchema = z.enum(["administrator", "operator", "support"]);
export type PlatformRole = z.infer<typeof PlatformRoleSchema>;

/** Fictional test parameters; this product must not publish them as production pricing. */
export const DREAMBOT_TEST_PLAN: CommercialPlanInput = {
  id: "prototype-only",
  version: 1,
  name: "测试套餐",
  price: { amountMinor: 1, currency: "CNY" },
  durationDays: 30,
  weeklyModelCreditMicros: 100_000,
  storageMib: 2048,
  memoryMib: 1024,
  cpuMillis: 1000,
  maxBots: 3,
  testOnly: true,
};
